import {
  Avatar,
  Box,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Text,
  useColorModeValue as mode,
} from "@chakra-ui/react";
import { FaStar, FaFacebook } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { formatDistanceToNow } from "date-fns";
import { Paper } from "./Paper.jsx";

export const REVIEW_SOURCE_URLS = {
  google: "https://g.page/r/CeuUpoDJPVzxEAg/review",
  facebook: "https://www.facebook.com/schnerp/reviews",
};

function StarRating({ rating }) {
  return (
    <HStack spacing="0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Box
          key={i}
          as={FaStar}
          color={i <= rating ? "warning" : "bg-muted"}
          boxSize="14px"
        />
      ))}
    </HStack>
  );
}

export function SourceIcon({ source, sourceUrl }) {
  const url = sourceUrl || REVIEW_SOURCE_URLS[source];
  if (source === "google") {
    return (
      <Link href={url} isExternal title="Google review">
        <Box as={FcGoogle} boxSize="14px" />
      </Link>
    );
  }
  if (source === "facebook") {
    return (
      <Link href={url} isExternal title="Facebook review">
        <Box as={FaFacebook} color="#1877F2" boxSize="14px" />
      </Link>
    );
  }
  return null;
}

export function ReviewCard({ review, variant = "compact", eager = false }) {
  if (variant === "compact") {
    return <CompactCard review={review} eager={eager} />;
  }
  return <FullCard review={review} eager={eager} />;
}

function CompactCard({ review, eager }) {
  return (
    <Paper p={{ base: 4, md: 5 }}>
      {/* Stars first — the fastest trust signal */}
      <HStack spacing={2} mb={2}>
        {review.source === "google" && review.rating && (
          <StarRating rating={review.rating} />
        )}
        {review.source === "facebook" && review.isRecommended && (
          <StarRating rating={5} />
        )}
      </HStack>

      {review.text && (
        <Text
          fontSize="md"
          noOfLines={6}
          mb={3}
          fontStyle="italic"
          color="emphasized"
        >
          &ldquo;{review.text}&rdquo;
        </Text>
      )}

      <HStack spacing={2}>
        <Avatar
          size="sm"
          name={review.reviewerName}
          src={review.reviewerAvatar}
        />
        <Text fontSize="sm" fontWeight="semibold" color="emphasized">
          {review.reviewerName}
        </Text>
        <SourceIcon source={review.source} sourceUrl={review.sourceUrl} />
      </HStack>
    </Paper>
  );
}

function FullCard({ review, eager }) {
  const replyBg = mode("bg-subtle", "bg-subtle");
  const relativeDate = formatDistanceToNow(new Date(review.reviewDate), {
    addSuffix: true,
  });

  return (
    <Paper p={{ base: 4, md: 6 }} display="inline-block" mb={4} w="100%">
      {/* Header */}
      <HStack spacing={3} mb={3}>
        <Avatar
          size="sm"
          name={review.reviewerName}
          src={review.reviewerAvatar}
          loading={eager ? "eager" : "lazy"}
        />
        <Box>
          <HStack spacing={2}>
            <Text fontWeight="semibold" fontSize="sm">
              {review.reviewerName}
            </Text>
            <SourceIcon source={review.source} sourceUrl={review.sourceUrl} />
          </HStack>
          <Text fontSize="xs" color="subtle">
            {relativeDate}
          </Text>
        </Box>
      </HStack>

      {/* Rating */}
      {review.source === "google" && review.rating && (
        <Box mb={3}>
          <StarRating rating={review.rating} />
        </Box>
      )}
      {review.source === "facebook" && review.isRecommended && (
        <Box mb={3}>
          <StarRating rating={5} />
        </Box>
      )}

      {/* Text */}
      {review.text ? (
        <Text fontSize="sm" whiteSpace="pre-wrap" mb={3}>
          {review.text}
        </Text>
      ) : (
        <Text fontSize="sm" fontStyle="italic" color="muted" mb={3}>
          This reviewer only left a rating.
        </Text>
      )}

      {/* Photos */}
      {review.photos?.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2} mb={3}>
          {review.photos.map((url, i) => (
            <Image
              key={i}
              src={url}
              alt={`Review photo ${i + 1}`}
              borderRadius="md"
              objectFit="cover"
              maxH="160px"
              w="100%"
              loading={eager ? "eager" : "lazy"}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Owner reply */}
      {review.ownerReply && (
        <Box
          bg={replyBg}
          borderLeftWidth="3px"
          borderLeftColor="brand.500"
          borderRadius="md"
          p={3}
          mt={2}
        >
          <Text fontSize="xs" fontWeight="semibold" mb={1}>
            Response from Schnerp
          </Text>
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {review.ownerReply}
          </Text>
        </Box>
      )}
    </Paper>
  );
}
